import { takeLatest, call, put, select } from 'redux-saga/effects';
import { ROUTE_USER, setUserInfo } from 'reducers/users';
import {
  showSnackbar,
  resetError,
  createError,
  toggleModal,
  setLoading,
  updatePage,
} from 'reducers/ui';
import { setCurrentTimezone } from 'reducers/utils';
import {
  GET_PROFILE,
  LOGIN,
  LOGOUT,
  ADD_NOTIFICATION_TOKEN,
  authenticated,
  UPDATE_USER_PROFILE,
} from 'reducers/auth';
import API from 'services/api';
import { loginErrorHandler, errorHandler } from 'utils/errorMessages';
import { PAGE_NAME, ROLES } from 'utils/constants';
import { updateURLQueryString } from './help';
import { getSecurityCompanyDetail } from 'reducers/securityCompany';
import { routeTheUserBasedOnSubscribedServices } from 'utils/helper/roles';
import {
  removeFormikLocalStorageKeys,
  removeEditFormStateKeys,
} from 'utils/valueChecker';
import { actions as themeActions } from 'slices/themes';
import { clearAllLists } from 'modules/dataGridTable/reducer';
import packageContents from '../../../package.json';
import { themeNames } from 'theme/base';
const { setGlobalTheme } = themeActions;

export function* addNotificationToken({ regionId, token }) {
  const notificationPayload = {
    app: 'PORTAL',
    appVersion: packageContents?.version,
    platform: 'WEB',
    regionId,
    token,
  };

  try {
    yield call(API.addNotificationToken, notificationPayload);
  } catch {
    console.log('err');
  }
}

// NOTE: no test for this
export function* loginUser({ user }) {
  try {
    yield put(setLoading(true));

    const { data } = yield call(API.login, user);
    // set last used theme
    const themeID = localStorage.getItem('theme');
    yield put(setGlobalTheme(themeID ? themeID : themeNames.SECURITY.id));
    yield put(setUserInfo(data.data));
    yield put(authenticated(true));
    yield call(getProfile);
    yield put(resetError());
  } catch (err) {
    yield put(createError(loginErrorHandler(err)));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: loginErrorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
  /* user data */
}

export function* getProfile() {
  try {
    const { data } = yield call(API.getProfile);
    // if we refresh the app, it should clear the local storage with formik form state
    removeFormikLocalStorageKeys();
    // eslint-disable-next-line new-cap
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    yield put(setCurrentTimezone(userTimezone));
    yield put(setUserInfo(data.data));
    yield put(authenticated(true));
  } catch (err) {
    yield put(authenticated(false));
    console.log('err');
  }
}
function eraseCookie(name) {
  document.cookie = `${name}=; Max-Age=0`;
}

export function* logout() {
  try {
    yield put(setLoading(true));
    const { status } = yield call(API.logout);
    if (status === 200) {
      yield put(authenticated(false));
      // set default theme
      yield put(setGlobalTheme(themeNames.SECURITY.id));
      window.sessionStorage.clear();
      eraseCookie('jwt');
      removeFormikLocalStorageKeys();
      removeEditFormStateKeys();
      // clear all the the pages of all the list data
      yield put(clearAllLists());
    }
    /*eslint-disable */
    // window.location.href = 'login';
  } catch (err) {
    /*eslint-disable */
    window.location.href = 'login';
    yield put(authenticated(false));
  }
  yield put(setLoading(false));
  /*eslint-enable */
}

export function* updateUserProfile({ data }) {
  try {
    yield put(setLoading(true));
    yield call(API.updateUserProfile, { data });
    if (data?.settings?.i18n) {
      //If it is just a language change then refresh the data in redux
      yield call(getProfile);
      yield put(setLoading(false));
      return;
    }
    yield put(toggleModal('profile'));
    yield call(getProfile);
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* routeUser({ user }) {
  const securityCompanyDetails = yield select(getSecurityCompanyDetail);
  const routeToService = routeTheUserBasedOnSubscribedServices(
    securityCompanyDetails,
  );
  // route the user depending on the role
  if (Object.keys(user).length) {
    const userRoles = user.roles;
    if (
      userRoles.includes(ROLES.SRM_ADMIN.id) ||
      userRoles.includes(ROLES.SRM_EMPLOYEE.id)
    ) {
      updateURLQueryString(PAGE_NAME.REGION);
      yield put(updatePage(PAGE_NAME.REGION));
    } else if (userRoles.includes(ROLES.SECURITY_COMPANY_MANAGER.id)) {
      updateURLQueryString(PAGE_NAME.ACCOUNT);
      yield put(updatePage(PAGE_NAME.ACCOUNT));
    } else if (userRoles.includes(ROLES.MANAGER.id)) {
      updateURLQueryString(PAGE_NAME.MANAGERIAL);
      yield put(updatePage(PAGE_NAME.MANAGERIAL));
    } else if (userRoles.includes(ROLES.SUPERVISOR.id) && routeToService) {
      updateURLQueryString(routeToService);
      yield put(updatePage(routeToService));
    }
  }
}

export function* userSagas() {
  yield takeLatest(GET_PROFILE, getProfile);
  yield takeLatest(ADD_NOTIFICATION_TOKEN, addNotificationToken);
  yield takeLatest(LOGIN, loginUser);
  yield takeLatest(LOGOUT, logout);
  yield takeLatest(UPDATE_USER_PROFILE, updateUserProfile);
  yield takeLatest(ROUTE_USER, routeUser);
}
